import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Nav from "../components/Nav";
import { api, formatApiError } from "../lib/api";
import { compressImage } from "../lib/imageCompress";
import { useAuth } from "../context/AuthContext";
import { Camera, CheckCircle, ArrowLeft, Eye, EyeSlash, Warning } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Activate() {
  const { code } = useParams();
  const { refresh } = useAuth();
  const nav = useNavigate();

  const [codeOk, setCodeOk] = useState(null); // null=loading, true=valid, false=already activated/missing
  const [codeError, setCodeError] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    skills: "",
    phone: "",
    bio: "",
    location: "",
    picture_url: "",
    email: "",
    password: "",
    password_confirm: "",
    popia_consent: false,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dataSaved, setDataSaved] = useState(null); // {originalKB, finalKB, savedPct}
  const [showPassword, setShowPassword] = useState(false);
  const [activated, setActivated] = useState(null); // null | {code, email, profileUrl, dashboardUrl}
  // Card type — drives which features the public profile renders.
  // Pre-selecting "curb_pro" because that's what 90% of paid sales are; user
  // can pick a different tile before submitting. Default also matches the
  // backend's fallback so we stay in sync if the field is ever missing.
  const [cardType, setCardType] = useState("curb_pro");

  useEffect(() => {
    // Background check — we render the form immediately (Feb 2026 pivot to
    // remove friction). If the code is already activated or missing we
    // surface the error inline; otherwise the user is already typing.
    (async () => {
      try {
        const { data } = await api.get(`/public/cardholder/${code}`);
        if (data.cardholder) {
          setCodeOk(false);
          setCodeError("This card has already been activated.");
        } else {
          setCodeOk(true);
        }
      } catch (err) {
        setCodeOk(false);
        setCodeError(formatApiError(err));
      }
    })();
  }, [code]);

  const handlePhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    setDataSaved(null);
    try {
      const originalKB = Math.round(file.size / 1024);
      const compressed = await compressImage(file);
      const finalKB = Math.round(compressed.size / 1024);
      if (compressed !== file) {
        setDataSaved({
          originalKB,
          finalKB,
          savedPct: Math.max(0, Math.round((1 - finalKB / originalKB) * 100)),
        });
      }
      const fd = new FormData();
      fd.append("code", code);
      fd.append("file", compressed);
      const { data } = await api.post("/public/activate/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, picture_url: data.url }));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name.trim()) return setError("Full name is required");
    const skills = form.skills.split(",").map((s) => s.trim()).filter(Boolean);
    if (skills.length === 0) return setError("Add at least one skill");
    if (!/^0\d{9}$/.test(form.phone)) return setError("Phone must be 10 digits starting with 0");
    if (!form.picture_url) return setError("A photo is required");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setError("Please enter a valid email");
    if (form.password.length < 6) return setError("Password must be at least 6 characters");
    if (form.password !== form.password_confirm) return setError("Passwords don't match");
    if (!form.popia_consent) return setError("Please tick the POPIA consent box");

    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/activate/${code}/direct`, {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        full_name: form.full_name,
        skills,
        phone: form.phone,
        bio: form.bio,
        location: form.location,
        picture_url: form.picture_url,
        popia_consent: form.popia_consent,
        card_type: cardType,
      });
      if (data.token) localStorage.setItem("cc_token", data.token);
      // Re-pull /auth/me so the global AuthContext knows the user is now logged in
      // (without this, clicking "Go to My Card" bounces back to /cardholder/login).
      if (data.token) await refresh();
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      if (data.verification_required) {
        nav(`/check-email?email=${encodeURIComponent(form.email.trim().toLowerCase())}`);
        return;
      }
      setActivated({
        code: data.code,
        email: form.email.trim().toLowerCase(),
        dashboardUrl: `${origin}/my-cards`,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (activated) {
    return <ActivationSuccess {...activated} />;
  }

  // Friction-free path (Feb 2026): we render the form immediately while the
  // background "is this code valid?" probe is in flight. Only the explicit
  // "already activated / not found" failure branch blocks the form.
  if (codeOk === false) {
    return (
      <div>
        <Nav />
        <section className="border-b border-black bg-[color:var(--cc-yellow)]">
          <div className="max-w-md mx-auto px-4 py-16 text-center">
            <h1 className="font-display font-black text-3xl">Can't activate this card</h1>
            <p className="mt-3 text-neutral-800">{codeError}</p>
            <Link to={`/c/${code}`} className="inline-flex mt-6 label-tag px-4 py-3 bg-black text-white brutal-btn">
              <ArrowLeft size={14} weight="bold" className="mr-2" /> Back to card
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-[color:var(--cc-bg-2)]">
        <div className="max-w-lg mx-auto px-4 sm:px-6 py-8">
          <Link to={`/c/${code}`} className="label-tag inline-flex items-center gap-2" data-testid="back-to-card">
            <ArrowLeft size={14} weight="bold" /> Back
          </Link>
          <div className="mt-4 label-tag text-neutral-500">Activating card</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1">
            #{(code || "").toUpperCase()}
          </h1>
          <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
            Peel off the sticker on the front of your card to reveal the QR. Fill in your details once — your card goes live the moment you tap Activate.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-3 bg-white border border-black p-5" data-testid="activate-direct-form">
            {/* Step 0: card type. Drives which features render on the
                public profile (tip jar / portfolio / book-a-meeting). User
                can change this any time later from their dashboard. */}
            <div>
              <label className="label-tag">What kind of card is this?</label>
              <div className="mt-1 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { id: "curb_crew",      title: "Curb Crew",      blurb: "Car guards · waiters · vendors" },
                  { id: "curb_pro",       title: "Curb Pro",       blurb: "Tradies · plumbers · mobile" },
                  { id: "curb_assets",    title: "Curb Retailer", blurb: "Shopfront · catalogue · stock" },
                  { id: "curb_medic",     title: "Curb Medic",     blurb: "Medical · emergency · pet ID" },
                  { id: "curb_traveller", title: "Curb Traveller", blurb: "Travel docs · trips · driver" },
                  { id: "curb_auto",      title: "Curb Auto",      blurb: "Vehicles · logbook · disc" },
                ].map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setCardType(t.id)}
                    className={`text-left border-2 border-black px-3 py-3 brutal-btn transition-transform ${cardType === t.id ? "bg-[color:var(--cc-yellow)]" : "bg-white"}`}
                    data-testid={`ad-card-type-${t.id}`}
                  >
                    <div className="font-display font-black text-base">{t.title}</div>
                    <div className="text-[10px] text-neutral-700 leading-tight mt-0.5">{t.blurb}</div>
                  </button>
                ))}
              </div>
              <div className="label-tag text-neutral-500 mt-1">You can change this later from your dashboard.</div>
            </div>
            <div>
              <label className="label-tag">Full name</label>
              <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="ad-name" required />
            </div>
            <div>
              <label className="label-tag">Skills (comma separated)</label>
              <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="plumber, painter" data-testid="ad-skills" required />
            </div>
            <div>
              <label className="label-tag">Your phone (kept private)</label>
              <input
                className="mt-1 w-full px-4 py-3 border border-black outline-none font-mono-cc"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9]/g, "") })}
                maxLength={10}
                inputMode="numeric"
                placeholder="0821234567"
                data-testid="ad-phone"
                required
              />
              <div className="label-tag text-neutral-500 mt-1">Visitors never see this. Used only to relay leads to you.</div>
            </div>
            <div>
              <label className="label-tag">Location (optional)</label>
              <input className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Bellville, Cape Town" data-testid="ad-location" />
            </div>
            <div>
              <label className="label-tag">Short bio (optional)</label>
              <textarea rows={3} className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="ad-bio" />
            </div>
            <div>
              <label className="label-tag">Your photo (required)</label>
              <div className="mt-2 flex items-start gap-3">
                <div className="w-24 h-24 border border-black bg-[color:var(--cc-bg-2)] grid place-items-center overflow-hidden flex-shrink-0">
                  {form.picture_url ? (
                    <img src={`${BACKEND}${form.picture_url}`} alt="" className="w-full h-full object-cover" data-testid="ad-photo-preview" />
                  ) : (
                    <Camera size={28} weight="bold" className="text-neutral-400" />
                  )}
                </div>
                <label className="label-tag px-4 py-3 border border-black brutal-btn cursor-pointer inline-flex items-center gap-2">
                  <Camera size={14} weight="bold" />
                  {uploading ? "Compressing & uploading…" : "Take / upload photo"}
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhoto(e.target.files?.[0])} data-testid="ad-photo-input" />
                </label>
              </div>
              <div className="label-tag text-neutral-500 mt-2">
                Photo is shrunk on your phone before upload — uses about as much data as one WhatsApp photo.
              </div>
              {dataSaved && (
                <div
                  className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-green-500 border-2 border-black label-tag text-black"
                  data-testid="ad-data-saved"
                >
                  Saved {dataSaved.savedPct}% data · {dataSaved.originalKB} KB → {dataSaved.finalKB} KB
                </div>
              )}
            </div>
            <div className="border-t pt-4">
              <label className="label-tag">Email (used to log back in later)</label>
              <input type="email" className="mt-1 w-full px-4 py-3 border border-black outline-none" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="ad-email" autoComplete="email" required />
              <div className="mt-2 flex items-start gap-2 p-3 bg-[color:var(--cc-yellow)] border-2 border-black" data-testid="email-warning">
                <Warning size={18} weight="bold" className="flex-shrink-0 mt-0.5" />
                <div className="text-xs leading-snug">
                  <strong>Double-check your email.</strong> If it's wrong you won't be able to log in to edit your card, get contact request notifications, or recover your password later.
                </div>
              </div>
            </div>
            <div>
              <label className="label-tag">Set a password</label>
              <div className="relative mt-1">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full px-4 py-3 pr-12 border border-black outline-none"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="ad-password"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-neutral-100"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="ad-password-toggle"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                </button>
              </div>
              <div className="label-tag text-neutral-500 mt-1">
                Tap the eye to check what you typed. At least 6 characters.
              </div>
            </div>
            <div>
              <label className="label-tag">Confirm password</label>
              <div className="relative mt-1">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full px-4 py-3 pr-12 border border-black outline-none"
                  value={form.password_confirm}
                  onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                  data-testid="ad-password-confirm"
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-neutral-100"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="ad-password-confirm-toggle"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                </button>
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={form.popia_consent} onChange={(e) => setForm({ ...form, popia_consent: e.target.checked })} data-testid="ad-popia" required />
              <span>I consent to my details being shown publicly on Curb Crew (POPIA). My phone number stays private.</span>
            </label>
            {error && <div className="label-tag text-red-600" data-testid="ad-error">{error}</div>}
            <button type="submit" disabled={submitting} className="w-full bg-[color:var(--cc-yellow)] label-tag px-4 py-4 brutal-btn flex items-center justify-center gap-2 disabled:opacity-60" data-testid="ad-submit">
              <CheckCircle size={16} weight="bold" /> {submitting ? "Activating…" : "Activate my card"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function ActivationSuccess({ code, email, dashboardUrl }) {
  return (
    <div>
      <Nav />
      <section className="border-b border-black bg-[color:var(--cc-yellow)]">
        <div className="max-w-lg mx-auto px-4 sm:px-6 py-12">
          <div className="inline-flex items-center gap-2 bg-black text-[color:var(--cc-yellow)] px-3 py-2 label-tag">
            <CheckCircle size={16} weight="bold" /> Your card is live
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-4">
            You're on Curb&nbsp;Crew.
          </h1>
          <p className="mt-3 text-neutral-800">
            Card <strong className="font-mono-cc">#{code}</strong> is now public.
          </p>

          <div className="mt-6 bg-white border-2 border-black p-4" data-testid="activate-success">
            <div className="label-tag text-neutral-500">Manage your card any time at</div>
            <code className="block mt-1 px-3 py-2 bg-[color:var(--cc-bg-2)] border border-black font-mono-cc text-sm break-all">
              {dashboardUrl}
            </code>
            <div className="label-tag text-neutral-500 mt-2">
              Sign in with <strong>{email}</strong> and the password you just set.
            </div>
          </div>

          <Link
            to="/my-cards?edit=first"
            className="mt-6 w-full bg-black text-white label-tag px-5 py-4 border-2 border-black brutal-btn flex items-center justify-center gap-2"
            data-testid="go-to-my-card-btn"
          >
            Set up my links & photo <CheckCircle size={16} weight="bold" />
          </Link>

          <p className="mt-6 text-xs text-neutral-700 leading-relaxed">
            Tip: bookmark <strong>{dashboardUrl}</strong> on your phone now. That's where you'll see contact requests, edit your card, or report it lost.
          </p>
        </div>
      </section>
    </div>
  );
}
